/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	Features Store - Subscription plan feature management
 **	Loads and caches namespace features from the backend
 */

import { create } from 'zustand'
import Config from '../services/config'

export interface ResolvedFeatures {
  namespaceId: string
  planId: string
  planName: string
  planVersion: number
  features: Record<string, any>
  overrides: Record<string, any>
  resolvedFeatures: Record<string, any>
}

interface FeaturesState {
  // Feature data
  features: ResolvedFeatures | null
  isLoading: boolean
  error: string | null
  lastLoadedAt: number | null

  // Actions
  loadFeatures: (namespaceId: string, token: string, forceRefresh?: boolean) => Promise<void>
  clearFeatures: () => void

  // Feature access utilities
  getFeature: <T = any>(featureName: string, defaultValue?: T) => T
  hasFeature: (featureName: string) => boolean
  isFeatureEnabled: (featureName: string) => boolean
  getFeatureNumber: (featureName: string, defaultValue?: number) => number
  getFeatureString: (featureName: string, defaultValue?: string) => string
  getFeatureDuration: (featureName: string, defaultValue?: string) => string
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

// Helper to check if cache is still valid
const isCacheValid = (lastFetch: number | null): boolean => {
  if (!lastFetch) return false
  return Date.now() - lastFetch < CACHE_DURATION
}

export const useFeaturesStore = create<FeaturesState>((set: any, get: any) => ({
  features: null,
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  loadFeatures: async (namespaceId: string, token: string, forceRefresh = false) => {
    const { lastLoadedAt, features, isLoading } = get()

    // Skip if already loading
    if (isLoading) {
      console.log('🎯 Features fetch already in progress, skipping...')
      return
    }

    // Skip if cache is valid for this namespace and not forcing refresh
    if (!forceRefresh && features?.namespaceId === namespaceId && isCacheValid(lastLoadedAt)) {
      console.log('🎯 Using cached features data')
      return
    }

    console.log(`🎯 Fetching features for namespace ${namespaceId}...`)
    set({ isLoading: true, error: null })

    try {
      // Strip auth0| or strata47| prefix if present
      const cleanToken = token.startsWith("auth0|")
        ? token.split("|")[1]
        : token.replace("strata47|", "")

      const response = await fetch(Config.STRATA47_OMNIIQ_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cleanToken}`
        },
        body: JSON.stringify({
          query: `
            query ResolveFeatures($namespaceId: String!) {
              resolveFeatures(namespaceId: $namespaceId) {
                namespaceId
                planId
                planName
                planVersion
                features
                overrides
                resolvedFeatures
              }
            }
          `,
          variables: { namespaceId }
        })
      })

      const data = await response.json()

      if (data.errors) {
        throw new Error(data.errors[0].message)
      }

      const resolvedFeatures = data.data?.resolveFeatures

      if (!resolvedFeatures) {
        // No plan assigned to namespace yet - use empty features
        console.warn(`🎯 No plan assigned to namespace ${namespaceId}`)
        set({
          features: {
            namespaceId,
            planId: '',
            planName: 'No Plan',
            planVersion: 0,
            features: {},
            overrides: {},
            resolvedFeatures: {}
          },
          isLoading: false,
          lastLoadedAt: Date.now(),
          error: null
        })
        return
      }

      set({
        features: resolvedFeatures,
        isLoading: false,
        error: null,
        lastLoadedAt: Date.now()
      })

      console.log(`🎯 Loaded features for namespace ${namespaceId}: Plan "${resolvedFeatures.planName}" v${resolvedFeatures.planVersion}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load features'
      console.error('🎯 Error loading features:', error)
      set({
        error: errorMessage,
        isLoading: false,
        features: null
      })
    }
  },

  clearFeatures: () => {
    console.log('🎯 Clearing features...')
    set({
      features: null,
      error: null,
      lastLoadedAt: null
    })
  },

  // Feature access utilities
  getFeature: <T = any>(featureName: string, defaultValue?: T): T => {
    const { features } = get()
    if (!features?.resolvedFeatures) {
      return defaultValue as T
    }

    const value = features.resolvedFeatures[featureName]
    return value !== undefined ? value : (defaultValue as T)
  },

  hasFeature: (featureName: string): boolean => {
    const { features } = get()
    if (!features?.resolvedFeatures) return false

    return featureName in features.resolvedFeatures
  },

  isFeatureEnabled: (featureName: string): boolean => {
    const { features } = get()
    if (!features?.resolvedFeatures) return false

    const value = features.resolvedFeatures[featureName]

    // Handle boolean features
    if (typeof value === 'boolean') return value

    // Handle string boolean values
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1'
    }

    // Handle number boolean values
    if (typeof value === 'number') return value !== 0

    return false
  },

  getFeatureNumber: (featureName: string, defaultValue: number = 0): number => {
    const { features } = get()
    if (!features?.resolvedFeatures) return defaultValue

    const value = features.resolvedFeatures[featureName]

    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      const parsed = parseFloat(value)
      return isNaN(parsed) ? defaultValue : parsed
    }

    return defaultValue
  },

  getFeatureString: (featureName: string, defaultValue: string = ''): string => {
    const { features } = get()
    if (!features?.resolvedFeatures) return defaultValue

    const value = features.resolvedFeatures[featureName]

    if (typeof value === 'string') return value
    if (value !== undefined && value !== null) return String(value)

    return defaultValue
  },

  getFeatureDuration: (featureName: string, defaultValue: string = '0'): string => {
    // Duration features are stored as strings (e.g., "30 days", "1 hour")
    return get().getFeatureString(featureName, defaultValue)
  }
}))

/**
 * Utility functions to access features outside of React components
 */
export const getFeatureValue = <T = any>(featureName: string, defaultValue?: T): T => {
  return useFeaturesStore.getState().getFeature(featureName, defaultValue)
}

export const isFeatureEnabled = (featureName: string): boolean => {
  return useFeaturesStore.getState().isFeatureEnabled(featureName)
}

export const hasFeature = (featureName: string): boolean => {
  return useFeaturesStore.getState().hasFeature(featureName)
}
